import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileSignature, RefreshCw, User, Building2, Briefcase, Eye, Mail, Phone,
  CreditCard, MapPin, CarFront, FileText, Send, Download, FileDown,
  ShieldCheck, AlertTriangle, XCircle, RotateCw, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOLOGIA_META = {
  privato:       { label: "Privato",       Icon: User,      bg: "bg-blue-50 text-blue-700 border-blue-200" },
  micro_impresa: { label: "Micro-impresa", Icon: Briefcase, bg: "bg-amber-50 text-amber-700 border-amber-200" },
  impresa:       { label: "Impresa",       Icon: Building2, bg: "bg-purple-50 text-purple-700 border-purple-200" },
};

const FIRMA_STATUS_META = {
  not_sent:           { label: "Non inviata",        color: "bg-zinc-100 text-zinc-600" },
  sent_for_signature: { label: "Inviata firma",      color: "bg-indigo-100 text-indigo-700" },
  identity_pending:   { label: "Attesa ID check",    color: "bg-amber-100 text-amber-700" },
  ongoing:            { label: "In corso",           color: "bg-blue-100 text-blue-700" },
  signed:             { label: "Firmata",            color: "bg-emerald-100 text-emerald-800" },
  declined:           { label: "Rifiutata",          color: "bg-red-100 text-red-700" },
  expired:            { label: "Scaduta",            color: "bg-orange-100 text-orange-700" },
  canceled:           { label: "Annullata",          color: "bg-zinc-200 text-zinc-700" },
  error:              { label: "Errore",             color: "bg-red-200 text-red-800" },
};

const STATI = [
  { value: "nuova",           label: "Nuova",               color: "bg-blue-50 text-blue-700" },
  { value: "in_revisione",    label: "In revisione",        color: "bg-amber-50 text-amber-700" },
  { value: "completata",      label: "Completata",          color: "bg-emerald-50 text-emerald-700" },
  { value: "firma_in_attesa", label: "Firma in attesa",     color: "bg-indigo-50 text-indigo-700" },
  { value: "firmata",         label: "Firmata",             color: "bg-emerald-100 text-emerald-800" },
  { value: "rifiutata",       label: "Rifiutata",           color: "bg-red-50 text-red-700" },
  { value: "archiviata",      label: "Archiviata",          color: "bg-zinc-50 text-zinc-600" },
];

export default function PraticheCreditoTab() {
  const [data, setData] = useState({ pratiche: [], total: 0, by_tipologia: {}, by_firma_status: {} });
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterFirma, setFilterFirma] = useState("");
  const [detail, setDetail] = useState(null);
  const [confirmSend, setConfirmSend] = useState(null); // pratica target conferma

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTipo) params.tipologia = filterTipo;
      if (filterStato) params.stato = filterStato;
      if (filterFirma) params.firma_status = filterFirma;
      const { data } = await axios.get(`${API}/admin/pratiche-credito`, { params, withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error("Errore caricamento pratiche");
    }
    setLoading(false);
  }, [filterTipo, filterStato, filterFirma]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/pratiche-credito/${id}`, { withCredentials: true });
      setDetail(data);
    } catch {
      toast.error("Errore apertura pratica");
    }
  };

  const updateStato = async (id, stato) => {
    try {
      await axios.patch(`${API}/admin/pratiche-credito/${id}`, { stato }, { withCredentials: true });
      toast.success("Stato aggiornato");
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const validateAndSend = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/pratiche-credito/${id}/validate-and-send-signature`,
        {},
        { withCredentials: true },
      );
      const fc = data.signature_fields_count;
      if (data.fallback_aes_standard) {
        toast.warning(`Firma inviata in modalita' AES standard (pre-identity non disponibile). ${fc} campi firma creati.`);
      } else {
        toast.success(`Firma inviata con pre-identity. ${fc} campi firma creati.`);
      }
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 412) {
        toast.error("Posizioni firma non validate. Scarica anteprima e conferma prima.");
      } else {
        toast.error(e?.response?.data?.detail || "Errore invio firma");
      }
    } finally {
      setConfirmSend(null);
    }
  };

  const previewPositions = (id) => {
    window.open(`${API}/admin/pratiche-credito/${id}/preview-signature-positions`, "_blank");
  };

  const confirmPositions = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/pratiche-credito/${id}/confirm-signature-positions`,
        {},
        { withCredentials: true },
      );
      toast.success(`Posizioni validate (${data.fields_count} campi firma)`);
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore validazione");
    }
  };

  const invalidatePositions = async (id) => {
    const reason = window.prompt("Motivo invalidazione (es. 'cambio template Ayvens'):", "");
    if (!reason) return;
    try {
      await axios.post(
        `${API}/admin/pratiche-credito/${id}/invalidate-signature-positions`,
        { reason },
        { withCredentials: true },
      );
      toast.success("Validazione resettata. Re-valida prima di re-inviare.");
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const syncSignature = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/pratiche-credito/${id}/sync-signature`,
        {},
        { withCredentials: true },
      );
      const ns = data?.reconcile?.new_status;
      toast.success(`Sync OK. Stato YouSign: ${ns || "-"}`);
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sync");
    }
  };

  const resendSignature = async (id) => {
    if (!window.confirm("Re-invia la firma? La precedente verrà annullata.")) return;
    try {
      await axios.post(`${API}/admin/pratiche-credito/${id}/resend-signature`, {}, { withCredentials: true });
      toast.success("Firma re-inviata");
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore re-invio");
    }
  };

  const cancelSignature = async (id) => {
    const reason = window.prompt("Motivo annullamento:", "Annullato da admin");
    if (!reason) return;
    try {
      await axios.post(
        `${API}/admin/pratiche-credito/${id}/cancel-signature`,
        { reason },
        { withCredentials: true },
      );
      toast.success("Firma annullata");
      fetchAll();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore annullamento");
    }
  };

  const inviaFirma = (id) => {
    const p = data.pratiche.find((x) => x.id === id) || detail;
    if (p) setConfirmSend(p);
    else validateAndSend(id);
  };

  return (
    <div className="space-y-4" data-testid="pratiche-credito-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-lg text-[#0F1F14] flex items-center gap-2">
            <FileSignature className="w-5 h-5" /> Pratiche di Credito Ayvens
          </h2>
          <p className="text-xs text-[#4A5E51]">
            Totale pratiche: <strong>{data.total}</strong>
            {" · "}Privati: <strong>{data.by_tipologia?.privato || 0}</strong>
            {" · "}Micro-imprese: <strong>{data.by_tipologia?.micro_impresa || 0}</strong>
            {" · "}Imprese: <strong>{data.by_tipologia?.impresa || 0}</strong>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} className="rounded-full">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterTipo || "__all__"} onValueChange={(v) => setFilterTipo(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Tutte tipologie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutte tipologie</SelectItem>
            <SelectItem value="privato">Privati</SelectItem>
            <SelectItem value="micro_impresa">Micro-imprese</SelectItem>
            <SelectItem value="impresa">Imprese</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStato || "__all__"} onValueChange={(v) => setFilterStato(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Tutti stati" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti stati</SelectItem>
            {STATI.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterFirma || "__all__"} onValueChange={(v) => setFilterFirma(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Stato firma" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti stati firma</SelectItem>
            {Object.entries(FIRMA_STATUS_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-zinc-500">Caricamento...</div>
        ) : data.pratiche.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">Nessuna pratica trovata.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Tipologia</th>
                <th className="text-left px-4 py-3">Denominazione</th>
                <th className="text-left px-4 py-3">CF/P.IVA</th>
                <th className="text-left px-4 py-3">Contatto</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Firma</th>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.pratiche.map((p) => {
                const meta = TIPOLOGIA_META[p.tipologia] || TIPOLOGIA_META.privato;
                const Icon = meta.Icon;
                const stato = STATI.find(s => s.value === p.stato) || STATI[0];
                const date = p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                return (
                  <tr key={p.id} className="hover:bg-zinc-50 transition-colors" data-testid={`pratica-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${meta.bg}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-[#0F1F14] truncate max-w-[200px]">{p.anagrafica?.denominazione || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600 font-mono">{p.anagrafica?.cf_piva || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-600">
                      <div>{p.anagrafica?.email || "—"}</div>
                      <div className="text-[10px] text-zinc-400">{p.anagrafica?.telefono || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${stato.color}`}>
                        {stato.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <FirmaStatusBadge status={p.firma_status} fallback={p.firma_fallback_aes_standard} />
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{date}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openDetail(p.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-[#163321] hover:bg-[#163321]/10"
                          data-testid={`pratica-view-${p.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Dettagli
                        </button>
                        {(!p.firma_status || p.firma_status === "not_sent") && (
                          <button
                            onClick={() => inviaFirma(p.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`pratica-send-${p.id}`}
                          >
                            <Send className="w-3 h-3" /> Invia firma
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog Dettaglio */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pratica {detail?.id}</DialogTitle>
          </DialogHeader>
          {detail && (
            <PraticaDetail
              p={detail}
              onUpdateStato={updateStato}
              onInviaFirma={inviaFirma}
              onSync={syncSignature}
              onResend={resendSignature}
              onCancel={cancelSignature}
              onPreview={previewPositions}
              onConfirmPositions={confirmPositions}
              onInvalidatePositions={invalidatePositions}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog conferma invio firma */}
      <Dialog open={!!confirmSend} onOpenChange={(o) => !o && setConfirmSend(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" /> Conferma invio firma
            </DialogTitle>
          </DialogHeader>
          {confirmSend && (
            <div className="space-y-4 text-sm">
              <p className="text-zinc-600">
                Stai per inviare la pratica per firma elettronica su YouSign. Il cliente ricevera' email con link + SMS OTP.
              </p>
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-2 text-xs">
                <div><strong>Tipologia:</strong> {TIPOLOGIA_META[confirmSend.tipologia]?.label || confirmSend.tipologia}</div>
                <div><strong>Cliente:</strong> {confirmSend.anagrafica?.denominazione}</div>
                <div><strong>Email:</strong> <span className="font-mono">{confirmSend.referente?.email || confirmSend.anagrafica?.email}</span></div>
                <div><strong>Cellulare:</strong> <span className="font-mono">{confirmSend.referente?.telefono || confirmSend.anagrafica?.telefono}</span></div>
                <div><strong>Veicolo:</strong> {[confirmSend.veicolo?.brand, confirmSend.veicolo?.model].filter(Boolean).join(" ") || "—"}</div>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                ⚠️ In ambiente sandbox, se YouSign non abilita pre-identity verification, la firma partira' in modalita' AES standard con fallback automatico.
              </p>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => setConfirmSend(null)}>Annulla</Button>
                <Button
                  size="sm"
                  onClick={() => validateAndSend(confirmSend.id)}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="btn-confirm-send"
                >
                  <Send className="w-4 h-4 mr-1" /> Conferma e invia
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FirmaStatusBadge({ status, fallback }) {
  const meta = FIRMA_STATUS_META[status] || FIRMA_STATUS_META.not_sent;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${meta.color}`}>
        {meta.label}
      </span>
      {fallback && (
        <span
          title="Pre-identity non disponibile — firma in modalita' AES standard"
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
        >
          ⚠ AES
        </span>
      )}
    </div>
  );
}

function PraticaDetail({ p, onUpdateStato, onInviaFirma, onSync, onResend, onCancel, onPreview, onConfirmPositions, onInvalidatePositions }) {
  const [sigInfo, setSigInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/admin/pratiche-credito/${p.id}/signature-info`, { withCredentials: true })
      .then(({ data }) => mounted && setSigInfo(data))
      .catch(() => {});
    return () => { mounted = false; };
  }, [p.id]);

  const validated = !!p.signatures_validated_by_admin;
  const canSend = validated && (!p.firma_status || p.firma_status === "not_sent");
  const fieldsCount = sigInfo?.fields_count ?? p.signature_fields_count ?? "—";
  const a = p.anagrafica || {};
  const r = p.referente || {};
  const i = p.impresa || {};
  const priv = p.privato || {};
  const v = p.veicolo || {};
  const f = p.fatturazione || {};
  const c = p.consensi || {};
  const tipoMeta = TIPOLOGIA_META[p.tipologia] || TIPOLOGIA_META.privato;
  const TipoIcon = tipoMeta.Icon;

  return (
    <div className="space-y-4 text-sm">
      {/* Header con tipologia */}
      <div className="flex items-center justify-between gap-4 flex-wrap pb-3 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <TipoIcon className="w-5 h-5 text-[#163321]" />
          <strong className="text-[#0F1F14]">{tipoMeta.label}</strong>
          <span className="text-xs text-zinc-500">· creata il {new Date(p.created_at).toLocaleString("it-IT")}</span>
        </div>
        <Select value={p.stato} onValueChange={(v) => onUpdateStato(p.id, v)}>
          <SelectTrigger className="w-48 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATI.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Anagrafica */}
      <Section title="Anagrafica" icon={User}>
        <InfoGrid>
          <Info label={p.tipologia === "privato" ? "Nome e Cognome" : "Ragione sociale"} value={a.denominazione} />
          <Info label="CF / P.IVA" value={a.cf_piva} mono />
          <Info label="IBAN" value={a.iban} mono wide />
          <Info label="Indirizzo" value={`${a.indirizzo}, ${a.cap} ${a.citta} (${a.provincia})`} wide />
          <Info label="Telefono" value={a.telefono} icon={Phone} />
          <Info label="Email" value={a.email} icon={Mail} />
          {a.pec && <Info label="PEC" value={a.pec} icon={Mail} />}
        </InfoGrid>
      </Section>

      {/* Referente */}
      <Section title="Referente comunicazioni" icon={User}>
        <InfoGrid>
          <Info label="Nome completo" value={`${r.titolo || ""} ${r.nome || ""} ${r.cognome || ""}`.trim()} />
          <Info label="CF" value={r.cf} mono />
          <Info label="Telefono" value={r.telefono} />
          <Info label="Email" value={r.email} />
        </InfoGrid>
      </Section>

      {/* Fatturazione (solo se diversa) */}
      {!f.stesso_indirizzo && (f.indirizzo || f.email) && (
        <Section title="Indirizzo fatturazione" icon={MapPin}>
          <InfoGrid>
            <Info label="Indirizzo" value={`${f.indirizzo}, ${f.cap} ${f.citta} (${f.provincia})`} wide />
            <Info label="Email" value={f.email} />
            <Info label="Telefono" value={f.telefono} />
          </InfoGrid>
        </Section>
      )}

      {/* Impresa / Privato */}
      {(p.tipologia === "impresa" || p.tipologia === "micro_impresa") && i && (
        <Section title="Dati aziendali" icon={Building2}>
          <InfoGrid>
            <Info label="Forma giuridica" value={i.forma_giuridica} />
            <Info label="REA" value={i.num_rea} />
            <Info label="CCIAA" value={i.camera_commercio} />
            <Info label="ATECO" value={i.settore_ateco} />
            <Info label="Anno costituzione" value={i.anno_costituzione} />
            <Info label="N° dipendenti" value={i.num_dipendenti} />
            <Info label="Fatturato annuo" value={i.fatturato_annuo_eur ? `€ ${Number(i.fatturato_annuo_eur).toLocaleString("it-IT")}` : ""} />
            {p.tipologia === "micro_impresa" && (
              <Info
                label="Autodichiarazione micro-impresa"
                value={i.autodichiarazione_micro === true ? "✓ Dichiara di appartenere" : i.autodichiarazione_micro === false ? "✗ Dichiara di NON appartenere" : "—"}
                wide
              />
            )}
          </InfoGrid>
        </Section>
      )}
      {p.tipologia === "privato" && priv && (
        <Section title="Dati personali" icon={User}>
          <InfoGrid>
            <Info label="Data nascita" value={priv.data_nascita} />
            <Info label="Luogo nascita" value={priv.luogo_nascita} />
            <Info label="Professione" value={priv.professione} />
            <Info label="Reddito mensile netto" value={priv.reddito_mensile_eur ? `€ ${Number(priv.reddito_mensile_eur).toLocaleString("it-IT")}` : ""} />
            <Info label="Stato civile" value={priv.stato_civile} />
            <Info label="Documento" value={[priv.documento_tipo, priv.documento_numero].filter(Boolean).join(" · ")} />
          </InfoGrid>
        </Section>
      )}

      {/* Veicolo */}
      {(v.brand || v.model) && (
        <Section title="Veicolo richiesto" icon={CarFront}>
          <InfoGrid>
            <Info label="Brand" value={v.brand} />
            <Info label="Modello" value={v.model} />
            <Info label="Canone mensile" value={v.canone_mensile ? `€ ${v.canone_mensile}` : ""} />
            <Info label="Durata" value={v.durata_mesi ? `${v.durata_mesi} mesi` : ""} />
            <Info label="Km annui" value={v.km_annui} />
            <Info label="Anticipo" value={v.anticipo ? `€ ${v.anticipo}` : ""} />
            {v.note && <Info label="Note" value={v.note} wide />}
          </InfoGrid>
        </Section>
      )}

      {/* Consensi */}
      <Section title="Consensi" icon={FileText}>
        <div className="grid sm:grid-cols-2 gap-1 text-xs">
          <Consenso label="Privacy" ok={c.privacy} />
          <Consenso label="Informazioni precontrattuali" ok={c.condizioni_precontrattuali} />
          <Consenso label="Informativa SIC" ok={c.informativa_sic} />
          <Consenso label="Dichiarazione guida & stato d'uso" ok={c.stato_uso_dichiarazione} />
          <Consenso label="Servizi aggiuntivi" ok={c.servizi_aggiuntivi} optional />
          <Consenso label="Marketing" ok={c.marketing} optional />
        </div>
      </Section>

      {/* Note */}
      {p.note && <Section title="Note del cliente"><p className="text-xs text-zinc-700">{p.note}</p></Section>}

      {/* Documenti Ayvens precompilati */}
      <DocumentiSection pratica={p} />

      {/* Validazione visuale posizioni firma (compliance) */}
      <Section title="Posizioni firma — Validazione admin (compliance)" icon={ShieldCheck}>
        <div className="space-y-3 text-xs">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-zinc-700">
              Questa pratica richiedera' <strong className="text-[#163321]">{fieldsCount} firme</strong>
              {sigInfo?.signable_templates && (
                <> su <strong>{sigInfo.signable_templates.length} documenti</strong></>
              )}
            </span>
            {sigInfo?.has_validation_required_fields && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                ⚠ Richiede ispezione manuale
              </span>
            )}
          </div>

          {sigInfo?.fields_by_role && Object.keys(sigInfo.fields_by_role).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(sigInfo.fields_by_role).map(([role, n]) => (
                <span key={role} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                  {role} ×{n}
                </span>
              ))}
            </div>
          )}

          {!validated && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-300">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-900 font-semibold mb-1">
                  ⚠️ Posizioni firma NON ancora validate visivamente
                </p>
                <p className="text-amber-800 text-[11px] leading-relaxed">
                  Per compliance normativa (TUB, D.Lgs 141/2010, D.Lgs 231/2007) DEVI scaricare il PDF
                  anteprima, controllare visivamente che ogni riquadro rosso sia sulla riga firma corretta,
                  e poi confermare prima di inviare al cliente.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => onPreview && onPreview(p.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-amber-600 text-white hover:bg-amber-700"
                  >
                    <FileText className="w-3.5 h-3.5" /> Scarica PDF anteprima posizioni
                  </button>
                  <button
                    onClick={() => onConfirmPositions && onConfirmPositions(p.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Conferma posizioni corrette
                  </button>
                </div>
              </div>
            </div>
          )}

          {validated && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-emerald-900 font-semibold">
                  ✅ Posizioni firma validate
                </p>
                <p className="text-emerald-800 text-[11px] mt-0.5">
                  Validate da <strong>{p.signatures_validated_by || "admin"}</strong>
                  {p.signatures_validated_at && <> il {new Date(p.signatures_validated_at).toLocaleString("it-IT")}</>}
                  {" "}({p.signatures_validated_count_snapshot || fieldsCount} campi firma).
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => onPreview && onPreview(p.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  >
                    <FileText className="w-3 h-3" /> Riapri anteprima
                  </button>
                  <button
                    onClick={() => onInvalidatePositions && onInvalidatePositions(p.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                  >
                    <RotateCw className="w-3 h-3" /> Invalida (cambio template)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Stato firma YouSign (Fase 2) */}
      <Section title="Firma elettronica YouSign" icon={FileSignature}>
        <div className="space-y-3 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <FirmaStatusBadge status={p.firma_status} fallback={p.firma_fallback_aes_standard} />
            {p.yousign_signature_request_id && (
              <span className="text-[10px] text-zinc-500 font-mono">
                ID: {p.yousign_signature_request_id.slice(0, 8)}...
              </span>
            )}
          </div>

          {p.firma_fallback_aes_standard && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-amber-800">
                <strong>Fallback AES standard applicato.</strong> Pre-identity verification non disponibile:
                contattare YouSign per abilitarla in produzione.
                {p.firma_fallback_reason && (
                  <div className="font-mono text-[10px] mt-1 opacity-70">{p.firma_fallback_reason}</div>
                )}
              </div>
            </div>
          )}

          {p.firma_sent_at && (
            <div>
              <strong>Inviata:</strong> {new Date(p.firma_sent_at).toLocaleString("it-IT")}
              {p.firma_signed_at && (
                <> · <strong>Firmata:</strong> {new Date(p.firma_signed_at).toLocaleString("it-IT")}</>
              )}
            </div>
          )}

          {p.firma_error_reason && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <strong>Errore:</strong> {p.firma_error_reason}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {p.firma_sign_url && ["sent_for_signature", "identity_pending", "ongoing"].includes(p.firma_status) && (
              <a
                href={p.firma_sign_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <FileSignature className="w-3.5 h-3.5" /> Apri link firma
              </a>
            )}
            {p.firma_status === "signed" && (
              <>
                <a
                  href={`${API}/admin/pratiche-credito/${p.id}/signed-pdf`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  <Download className="w-3.5 h-3.5" /> PDF firmato
                </a>
                <a
                  href={`${API}/admin/pratiche-credito/${p.id}/audit-trail`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-zinc-700 text-white hover:bg-zinc-800"
                >
                  <FileText className="w-3.5 h-3.5" /> Audit trail
                </a>
              </>
            )}
            {p.yousign_signature_request_id && p.firma_status !== "signed" && (
              <button
                onClick={() => onSync && onSync(p.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium border border-zinc-300 hover:bg-zinc-50"
              >
                <RotateCw className="w-3.5 h-3.5" /> Sync stato
              </button>
            )}
            {["declined", "expired", "canceled", "error"].includes(p.firma_status) && (
              <button
                onClick={() => onResend && onResend(p.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Re-invia firma
              </button>
            )}
            {["sent_for_signature", "identity_pending", "ongoing"].includes(p.firma_status) && (
              <button
                onClick={() => onCancel && onCancel(p.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-medium border border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5" /> Annulla firma
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 pt-4 border-t border-zinc-200 flex-wrap">
        <div className="text-[10px] text-zinc-400">IP: {p.ip_masked} · Aggiornato: {p.updated_at ? new Date(p.updated_at).toLocaleString("it-IT") : "—"}</div>
        {(!p.firma_status || p.firma_status === "not_sent") && (
          <Button
            size="sm"
            onClick={() => canSend && onInviaFirma(p.id)}
            disabled={!canSend}
            title={!validated ? "Valida prima le posizioni firma" : ""}
            className={`rounded-full ${canSend ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-zinc-200 text-zinc-400 cursor-not-allowed"}`}
            data-testid="pratica-invia-firma"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Valida e invia a firma
          </Button>
        )}
      </div>
    </div>
  );
}

// ============== UI helpers ==============
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-zinc-50 rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#163321] mb-2 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{title}
      </p>
      {children}
    </div>
  );
}
function InfoGrid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">{children}</div>;
}
function Info({ label, value, mono, wide, icon: Icon }) {
  if (!value) return null;
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</p>
      <p className={`text-xs text-zinc-800 flex items-center gap-1 ${mono ? "font-mono" : ""}`}>
        {Icon && <Icon className="w-3 h-3 text-zinc-400" />}{value}
      </p>
    </div>
  );
}
function Consenso({ label, ok, optional }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[9px] font-bold ${ok ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-500"}`}>
        {ok ? "✓" : "—"}
      </span>
      <span className="text-zinc-700">{label}{optional && <span className="text-zinc-400 text-[10px]"> (opz.)</span>}</span>
    </div>
  );
}

function DocumentiSection({ pratica }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/pratiche-credito/${pratica.id}/documenti`,
          { withCredentials: true },
        );
        if (!aborted) setDocs(data?.documenti || []);
      } catch {
        if (!aborted) setDocs([]);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [pratica.id]);

  const downloadUrl = (key) => `${API}/admin/pratiche-credito/${pratica.id}/pdf/${key}`;
  const zipUrl = `${API}/admin/pratiche-credito/${pratica.id}/pdf-zip`;

  return (
    <div className="bg-[#F5F8F6] border border-[#163321]/20 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[#163321] flex items-center gap-1">
          <FileDown className="w-3 h-3" /> Documenti Ayvens precompilati
        </p>
        <a
          href={zipUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 bg-white hover:border-[#163321] text-xs font-medium text-[#163321] transition-colors"
          data-testid="download-all-zip"
        >
          <Download className="w-3.5 h-3.5" /> Scarica tutti (ZIP)
        </a>
      </div>
      {loading ? (
        <p className="text-xs text-zinc-500">Caricamento documenti...</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-zinc-500">Nessun documento disponibile.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* COLONNA SX: Moduli precompilati */}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#163321] mb-2 flex items-center gap-1.5 pb-2 border-b border-[#163321]/20">
              <FileDown className="w-3 h-3" /> Moduli precompilati
              <span className="ml-auto text-[9px] text-zinc-500 font-normal normal-case">dati pratica applicati</span>
            </p>
            <div className="space-y-2">
              {docs.filter(d => d.filled).map((d) => (
                <a
                  key={d.key}
                  href={downloadUrl(d.key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-white border border-[#163321]/20 hover:border-[#163321] text-left transition-colors group"
                  data-testid={`download-${d.key}`}
                >
                  <FileText className="w-4 h-4 text-[#163321] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#0F1F14] truncate">{d.name}</p>
                    <p className="text-[10px] text-[#163321]/70">Precompilato ✓</p>
                  </div>
                  <Download className="w-3.5 h-3.5 text-zinc-400 group-hover:text-[#D97B58]" />
                </a>
              ))}
              {docs.filter(d => d.filled).length === 0 && (
                <p className="text-[11px] text-zinc-400 italic p-2">Nessun modulo precompilato per questa tipologia.</p>
              )}
            </div>
          </div>

          {/* COLONNA DX: Informative da firmare */}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#8B6100] mb-2 flex items-center gap-1.5 pb-2 border-b border-[#E8D3A0]">
              <FileText className="w-3 h-3" /> Informative da firmare
              <span className="ml-auto text-[9px] text-zinc-500 font-normal normal-case">lettura e firma manuale</span>
            </p>
            <div className="space-y-2">
              {docs.filter(d => !d.filled).map((d) => (
                <a
                  key={d.key}
                  href={downloadUrl(d.key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-white border border-[#E8D3A0]/60 hover:border-[#8B6100] text-left transition-colors group"
                  data-testid={`download-${d.key}`}
                >
                  <FileText className="w-4 h-4 text-[#8B6100] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#0F1F14] truncate">{d.name}</p>
                    <p className="text-[10px] text-[#8B6100]/70">Da far firmare al cliente</p>
                  </div>
                  <Download className="w-3.5 h-3.5 text-zinc-400 group-hover:text-[#8B6100]" />
                </a>
              ))}
              {docs.filter(d => !d.filled).length === 0 && (
                <p className="text-[11px] text-zinc-400 italic p-2">Nessuna informativa per questa tipologia.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <p className="text-[10px] text-zinc-500 mt-3 italic">
        I moduli precompilati contengono i dati della pratica inseriti dal cliente. Le informative vanno scaricate e fatte firmare al cliente (in futuro tramite YouSign).
      </p>
    </div>
  );
}

